"use client";

// Sticky wrapper for the chapter nav that expands to the full viewport width
// once it sticks (design QA): at rest the bar matches the main container
// (1280px cap, scaling margins); scrolled into its stuck state it grows
// edge-to-edge. A zero-height sentinel above the sticky element marks the
// stick point — when its top crosses above the sticky offset, the nav is stuck.

import * as React from "react";

import { cn } from "@/lib/utils";

/** Hysteresis buffer so slow scroll near the stick line doesn't flip stuck state. */
const STUCK_HYSTERESIS_PX = 10;

/** Fallback when --chapter-position is not yet synced (SSR / before mount). */
const DEFAULT_CHAPTER_POSITION_PX = 73;

function readChapterPosition(): number {
  if (typeof window === "undefined") return DEFAULT_CHAPTER_POSITION_PX;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--chapter-position")
    .trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_CHAPTER_POSITION_PX;
}

export function StickyExpand({ children }: { children: React.ReactNode }) {
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = React.useState(false);
  const stickyOffsetRef = React.useRef(DEFAULT_CHAPTER_POSITION_PX);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const refreshOffset = () => {
      stickyOffsetRef.current = readChapterPosition();
    };

    const update = () => {
      const stickyOffset = stickyOffsetRef.current;
      const top = sentinel.getBoundingClientRect().top;
      setStuck((prev) =>
        prev ? top < stickyOffset + STUCK_HYSTERESIS_PX : top < stickyOffset
      );
    };

    const onLayoutChange = () => {
      refreshOffset();
      update();
    };

    refreshOffset();
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", onLayoutChange);
    window.addEventListener("announcement-h-change", onLayoutChange);
    const header = document.querySelector("header");
    header?.addEventListener("transitionend", onLayoutChange);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", onLayoutChange);
      window.removeEventListener("announcement-h-change", onLayoutChange);
      header?.removeEventListener("transitionend", onLayoutChange);
    };
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden />
      <div className="sticky top-[var(--chapter-position)] z-20">
        <div
          className={cn(
            "mx-auto transition-[max-width] duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
            stuck
              ? "max-w-full"
              : "max-w-full md:max-w-[calc(100%-80px)] lg:max-w-[min(1280px,calc(100%-160px))]"
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}
